// eslint-disable-next-line import/no-extraneous-dependencies
import { Card, LoadingSpinner } from '@postscript/components';
import { useQuery } from '@tanstack/react-query';
import { api } from 'controllers/network/apiClient';
import { useIdentifyLogRocketUser } from 'hooks/useLogRocket';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export type OmniInstance = 'POSTSCRIPT' | 'FONDUE';

const allowedOrigins = [
  'https://omni.postscript.io',
  'https://postscript.embed-omniapp.co',
  'https://omni.getfondue.com',
];

interface GetSignedURLParams {
  id: string;
  sourceType?: string;
  flowId?: string;
  omniInstance?: OmniInstance;
}

function getSignedURL({
  id,
  sourceType,
  flowId,
  omniInstance,
}: GetSignedURLParams) {
  const params = new URLSearchParams({ dashboard_id: id });

  if (sourceType) params.set('omni_source_type', sourceType);
  if (flowId) params.set('omni_type_id', flowId);
  if (omniInstance) params.set('omni_instance', omniInstance);

  return api.get(`/v2/analytics/omni/signed_url?${params.toString()}`);
}

function setFrameHeight(iframe: HTMLIFrameElement, height: number) {
  // eslint-disable-next-line no-param-reassign
  iframe.style.height = `${height + 48}px`;
}

const StyledFrame = styled.iframe`
  width: 100%;
`;

const StyledLoader = styled(Card)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 360px;
  width: 100%;
`;

interface Props {
  id: string;
  sourceType?: string;
  flowId?: string;
  omniInstance?: OmniInstance;
}

export default function OmniDashboard({
  id,
  sourceType,
  flowId,
  omniInstance = 'POSTSCRIPT',
}: Props): JSX.Element {
  const ref = useRef<HTMLIFrameElement>(null);
  const [url, setURL] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
  }, [id]);

  useIdentifyLogRocketUser('ssovkv/omni-dashboard');

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      // Only accept size event messages from the Omni instance
      if (
        !allowedOrigins.includes(event.origin) ||
        event.data.name !== 'size' ||
        !ref.current
      ) {
        return;
      }

      const { height } = event.data.payload;
      setFrameHeight(ref.current, height);
    };

    window.addEventListener('message', listener);

    return () => window.removeEventListener('message', listener);
  }, [ref]);

  useQuery({
    queryKey: ['omniSignedURL', id],
    queryFn: () => getSignedURL({ id, sourceType, flowId, omniInstance }),
    select: ({ url }) => url,
    onSuccess: setURL, // TODO: useQuery callbacks removed in v5
    staleTime: 0,
  });

  return (
    <>
      {isLoading && (
        <StyledLoader>
          <LoadingSpinner size="medium">Loading...</LoadingSpinner>
        </StyledLoader>
      )}
      <StyledFrame
        ref={ref}
        title="Omni Dashboard"
        src={url}
        allow="clipboard-write"
        referrerPolicy="origin"
        onLoad={() => setIsLoading(false)}
        hidden={isLoading}
      />
    </>
  );
}
